import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, List, ListItem, ListItemAvatar,
  ListItemText, Avatar, Chip, Paper, Alert, Button, CircularProgress,
} from '@mui/material';
import {
  LocalShipping, SignalCellularAlt, SignalCellularOff, Place, Flag, MyLocation, Refresh,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { locationAPI } from '../services/api';

const RAW_API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const SOCKET_URL = RAW_API.replace(/\/api\/?$/, '');

const ACTIVE_STATUS_LABEL = { assigned: '배차완료', in_progress: '배송중' };

// Kakao Maps SDK 로더 (services 라이브러리 포함 — Geocoder 사용)
let kakaoLoadPromise = null;
function loadKakaoMaps() {
  if (kakaoLoadPromise) return kakaoLoadPromise;
  kakaoLoadPromise = new Promise((resolve, reject) => {
    const tryLoad = () => {
      if (window.kakao && window.kakao.maps && typeof window.kakao.maps.load === 'function') {
        window.kakao.maps.load(() => {
          if (window.kakao.maps.LatLng) resolve(window.kakao);
          else reject(new Error('Kakao Maps SDK 초기화 후에도 LatLng 을 찾을 수 없음'));
        });
        return true;
      }
      return false;
    };
    if (tryLoad()) return;
    const start = Date.now();
    const check = setInterval(() => {
      if (tryLoad()) { clearInterval(check); return; }
      if (Date.now() - start > 10000) {
        clearInterval(check);
        reject(new Error('Kakao Maps SDK load timeout'));
      }
    }, 200);
  });
  return kakaoLoadPromise;
}

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

function LiveTracking() {
  const [drivers, setDrivers] = useState([]);
  const [connected, setConnected] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [geoVersion, setGeoVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [fetchErr, setFetchErr] = useState('');

  const socketRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const driverMarkersRef = useRef({});  // driver_id → { marker, overlay, contentEl, pos }
  const routeLayersRef = useRef({});     // driver_id → { pickup, dropoff, line }
  const geoCacheRef = useRef(new Map()); // address → {lat,lng} | 'pending' | 'fail'
  const geocoderRef = useRef(null);
  const boundsFitRef = useRef(false);

  // 지도 초기화
  useEffect(() => {
    let cancelled = false;
    loadKakaoMaps()
      .then((kakao) => {
        if (cancelled || !mapContainerRef.current) return;
        mapRef.current = new kakao.maps.Map(mapContainerRef.current, {
          center: new kakao.maps.LatLng(37.5665, 126.9780),
          level: 8,
        });
        if (kakao.maps.services) geocoderRef.current = new kakao.maps.services.Geocoder();
        setMapReady(true);
      })
      .catch((err) => {
        console.error('카카오맵 로드 실패:', err);
        setMapError(err.message || '지도를 불러오지 못했습니다.');
      });
    return () => { cancelled = true; };
  }, []);

  // 서버에서 전체 운전자 위치 + 진행중 주문 조회
  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await locationAPI.getDriverLocations();
      const rows = res.data?.data || [];
      setFetchErr('');
      setLastSync(new Date());
      setDrivers((prev) => {
        const prevById = {};
        prev.forEach((p) => { prevById[p.driver_id] = p; });
        return rows.map((r) => {
          const old = prevById[r.driver_id];
          // 소켓으로 더 최신 좌표가 들어와 있으면 그것을 유지
          const useSocket = old && old._socketAt &&
            (!r.location_updated_at || new Date(old._socketAt) > new Date(r.location_updated_at));
          return {
            ...r,
            latitude: useSocket ? old.latitude : num(r.location_lat),
            longitude: useSocket ? old.longitude : num(r.location_lng),
            _socketAt: useSocket ? old._socketAt : null,
            lastUpdate: r.location_updated_at || (old && old.lastUpdate) || null,
          };
        });
      });
    } catch (e) {
      console.error('운전자 위치 조회 실패:', e);
      setFetchErr(e.response?.data?.message || e.message || '운전자 위치를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
    const id = setInterval(fetchDrivers, 20000);
    return () => clearInterval(id);
  }, [fetchDrivers]);

  // Socket.io — 실시간 좌표 패치
  useEffect(() => {
    socketRef.current = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current.on('connect', () => {
      setConnected(true);
      socketRef.current.emit('join-admin');
    });
    socketRef.current.on('disconnect', () => setConnected(false));
    socketRef.current.on('driver-location', (data) => {
      const lat = num(data.latitude ?? data.location_lat);
      const lng = num(data.longitude ?? data.location_lng);
      if (lat == null || lng == null) return;
      setDrivers((prev) => {
        const i = prev.findIndex((d) => d.driver_id === data.driver_id);
        if (i >= 0) {
          const u = [...prev];
          u[i] = { ...u[i], latitude: lat, longitude: lng, lastUpdate: new Date().toISOString(), _socketAt: new Date().toISOString() };
          return u;
        }
        return [...prev, {
          driver_id: data.driver_id, driver_name: data.name || data.driver_name,
          vehicle_number: data.vehicle_number, latitude: lat, longitude: lng,
          lastUpdate: new Date().toISOString(), _socketAt: new Date().toISOString(),
        }];
      });
    });
    return () => { if (socketRef.current) socketRef.current.disconnect(); };
  }, []);

  // 주소 → 좌표 (좌표 없을 때만). 결과는 캐시하고 geoVersion 증가로 재렌더 유도
  const resolvePoint = useCallback((addr, lat, lng) => {
    const la = num(lat), ln = num(lng);
    if (la != null && ln != null) return { lat: la, lng: ln };
    if (!addr || !geocoderRef.current) return null;
    const cached = geoCacheRef.current.get(addr);
    if (cached && typeof cached === 'object') return cached;
    if (cached === 'pending' || cached === 'fail') return null;
    geoCacheRef.current.set(addr, 'pending');
    geocoderRef.current.addressSearch(addr, (result, status) => {
      const kakao = window.kakao;
      if (status === kakao.maps.services.Status.OK && result[0]) {
        geoCacheRef.current.set(addr, { lat: parseFloat(result[0].y), lng: parseFloat(result[0].x) });
      } else {
        geoCacheRef.current.set(addr, 'fail');
      }
      setGeoVersion((v) => v + 1);
    });
    return null;
  }, []);

  // 마커 + 경로 갱신
  useEffect(() => {
    if (!mapReady || !window.kakao || !mapRef.current) return;
    const kakao = window.kakao;
    const map = mapRef.current;
    const bounds = new kakao.maps.LatLngBounds();
    let hasPt = false;
    const seen = new Set();

    const badge = (text, bg) => {
      const el = document.createElement('div');
      el.style.cssText = `background:${bg};color:#fff;padding:3px 8px;border-radius:10px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.3);transform:translateY(-26px);`;
      el.textContent = text;
      return el;
    };

    drivers.forEach((d) => {
      seen.add(String(d.driver_id));
      const dLat = num(d.latitude), dLng = num(d.longitude);

      // 운전자 마커
      if (dLat != null && dLng != null) {
        const pos = new kakao.maps.LatLng(dLat, dLng);
        bounds.extend(pos); hasPt = true;
        let e = driverMarkersRef.current[d.driver_id];
        if (!e) {
          const marker = new kakao.maps.Marker({ position: pos, map });
          const el = badge(`🚚 ${d.driver_name || '운전자'} ${d.vehicle_number || ''}`.trim(), '#1976d2');
          const overlay = new kakao.maps.CustomOverlay({ position: pos, content: el, yAnchor: 1, map });
          driverMarkersRef.current[d.driver_id] = { marker, overlay, contentEl: el, pos };
        } else {
          e.marker.setPosition(pos);
          e.overlay.setPosition(pos);
          e.contentEl.textContent = `🚚 ${d.driver_name || '운전자'} ${d.vehicle_number || ''}`.trim();
          e.pos = pos;
        }
      }

      // 진행중 주문의 상차지/하차지 + 경로
      let rl = routeLayersRef.current[d.driver_id];
      const clearRoute = () => {
        if (rl) {
          rl.pickup && rl.pickup.setMap(null);
          rl.dropoff && rl.dropoff.setMap(null);
          rl.line && rl.line.setMap(null);
          delete routeLayersRef.current[d.driver_id];
        }
      };

      if (d.order_id) {
        const p = resolvePoint(d.origin_address, d.origin_lat, d.origin_lng);
        const q = resolvePoint(d.destination_address, d.destination_lat, d.destination_lng);
        clearRoute();
        rl = {};
        const path = [];
        if (dLat != null && dLng != null) path.push(new kakao.maps.LatLng(dLat, dLng));
        if (p) {
          const pp = new kakao.maps.LatLng(p.lat, p.lng);
          bounds.extend(pp); hasPt = true;
          path.push(pp);
          rl.pickup = new kakao.maps.CustomOverlay({ position: pp, content: badge(`📦 상차 ${d.order_number || ''}`.trim(), '#2e7d32'), yAnchor: 1, map });
        }
        if (q) {
          const qq = new kakao.maps.LatLng(q.lat, q.lng);
          bounds.extend(qq); hasPt = true;
          path.push(qq);
          rl.dropoff = new kakao.maps.CustomOverlay({ position: qq, content: badge('🏁 하차', '#d32f2f'), yAnchor: 1, map });
        }
        if (path.length >= 2) {
          rl.line = new kakao.maps.Polyline({
            path, strokeWeight: 4, strokeColor: '#ff6f00', strokeOpacity: 0.85, strokeStyle: 'shortdash', map,
          });
        }
        routeLayersRef.current[d.driver_id] = rl;
      } else {
        clearRoute();
      }
    });

    // 사라진 운전자 정리
    Object.keys(driverMarkersRef.current).forEach((id) => {
      if (!seen.has(String(id))) {
        const e = driverMarkersRef.current[id];
        e.marker.setMap(null); e.overlay.setMap(null);
        delete driverMarkersRef.current[id];
        const r = routeLayersRef.current[id];
        if (r) { r.pickup && r.pickup.setMap(null); r.dropoff && r.dropoff.setMap(null); r.line && r.line.setMap(null); delete routeLayersRef.current[id]; }
      }
    });

    // 처음 데이터가 들어오면 전체가 보이도록 한 번 맞춤
    if (hasPt && !boundsFitRef.current) {
      map.setBounds(bounds);
      boundsFitRef.current = true;
    }
  }, [drivers, mapReady, geoVersion, resolvePoint]);

  const fitAll = () => {
    if (!window.kakao || !mapRef.current) return;
    const kakao = window.kakao;
    const bounds = new kakao.maps.LatLngBounds();
    let has = false;
    Object.values(driverMarkersRef.current).forEach((e) => { bounds.extend(e.pos); has = true; });
    Object.values(routeLayersRef.current).forEach((r) => {
      if (r.pickup) { bounds.extend(r.pickup.getPosition()); has = true; }
      if (r.dropoff) { bounds.extend(r.dropoff.getPosition()); has = true; }
    });
    if (has) mapRef.current.setBounds(bounds);
  };

  const focusDriver = (driver_id) => {
    const e = driverMarkersRef.current[driver_id];
    if (e && mapRef.current) { mapRef.current.setLevel(4); mapRef.current.panTo(e.pos); }
  };

  const getTimeSince = (date) => {
    if (!date) return '-';
    const s = Math.floor((Date.now() - new Date(date)) / 1000);
    if (s < 0) return '방금';
    if (s < 60) return `${s}초 전`;
    if (s < 3600) return `${Math.floor(s / 60)}분 전`;
    if (s < 86400) return `${Math.floor(s / 3600)}시간 전`;
    return `${Math.floor(s / 86400)}일 전`;
  };

  const onDelivery = drivers.filter((d) => d.order_id).length;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
          <Typography variant="h5" fontWeight={600}>실시간 추적</Typography>
          {lastSync && (
            <Typography variant="caption" color="textSecondary">
              마지막 갱신 {lastSync.toLocaleTimeString('ko-KR')}
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Button size="small" variant="outlined" disabled={loading} onClick={fetchDrivers}
            startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}>
            {loading ? '새로고침 중...' : '새로고침'}
          </Button>
          <Button size="small" variant="outlined" startIcon={<MyLocation />} onClick={fitAll}>전체 보기</Button>
          <Chip
            icon={connected ? <SignalCellularAlt /> : <SignalCellularOff />}
            label={connected ? '연결됨' : '연결 끊김'}
            color={connected ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>
      </Box>

      {fetchErr && <Alert severity="error" sx={{ mb: 2 }}>{fetchErr}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        운전자 {drivers.length}명 표시 중 · 배송 진행 {onDelivery}건.
        🚚 운전자 · 📦 상차지 · 🏁 하차지 · 점선은 운행 경로입니다.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ height: 560, position: 'relative', overflow: 'hidden' }}>
            <Box ref={mapContainerRef} sx={{ width: '100%', height: '100%' }} />
            {!mapReady && !mapError && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#e3f2fd' }}>
                <Typography color="textSecondary">지도를 불러오는 중...</Typography>
              </Box>
            )}
            {mapError && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#fff3e0', p: 3, textAlign: 'center' }}>
                <Typography color="error">지도 로드 실패: {mapError}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: 560, overflow: 'auto' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>운전자 ({drivers.length})</Typography>
              {drivers.length === 0 ? (
                <Box textAlign="center" py={5}>
                  <LocalShipping sx={{ fontSize: 60, color: '#ccc' }} />
                  <Typography color="textSecondary" sx={{ mt: 2 }}>위치가 전송된 운전자가 없습니다.</Typography>
                </Box>
              ) : (
                <List dense>
                  {drivers.map((d) => (
                    <ListItem key={d.driver_id} divider alignItems="flex-start"
                      sx={{ cursor: 'pointer' }} onClick={() => focusDriver(d.driver_id)}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: d.order_id ? '#2e7d32' : '#1976d2' }}>
                          <LocalShipping />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={700}>{d.driver_name || `운전자 ${d.driver_id}`}</Typography>
                            <Typography variant="caption" color="textSecondary">{d.vehicle_number || ''}</Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            {d.order_id ? (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip size="small" color="success" variant="outlined"
                                  label={`${d.order_number || '주문'} · ${ACTIVE_STATUS_LABEL[d.order_status] || d.order_status || ''}`}
                                  sx={{ mb: 0.5 }} />
                                <Box display="flex" alignItems="flex-start" gap={0.5}>
                                  <Place sx={{ fontSize: 14, color: '#2e7d32', mt: '2px' }} />
                                  <Typography variant="caption">{d.origin_address || '-'}</Typography>
                                </Box>
                                <Box display="flex" alignItems="flex-start" gap={0.5}>
                                  <Flag sx={{ fontSize: 14, color: '#d32f2f', mt: '2px' }} />
                                  <Typography variant="caption">{d.destination_address || '-'}</Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="textSecondary">대기 중 (배송 없음)</Typography>
                            )}
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                              위치: {getTimeSince(d.lastUpdate)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default LiveTracking;
